<template>
	<v-card-title v-if="title" class="d-flex align-center">
		{{ title }}
		<v-icon color="info" @click="load">$reload</v-icon>
	</v-card-title>
	<v-card-text>
		<v-form @submit.prevent="search" class="form-inline">
			<v-row class="">
				<v-col cols="12" class="d-flex flex-wrap align-center">
					<bean-selector v-model="query.bean_id" class="mr-1" @selected="onBeanSelected" />
					{{ bean?.name }}
					<v-text-field
						outlined
						dense
						v-model="query.name"
						label="名称"
						hide-details
						class="search-field-name mr-1"
					/>
					<v-select
						outlined
						dense
						label="状态"
						v-model="query.status"
						:items="refs['RoastRecipeStatus']"
						hide-details
						class="mr-1"
						clearable
					></v-select>
					<v-btn color="primary" @click="search" class="mr-1">
						<v-icon>$search</v-icon>
						查询
					</v-btn>
					<v-icon v-if="queryPairs.length > 0" class="mr-1" @click="reset">$close</v-icon>
				</v-col>
			</v-row>
		</v-form>
	</v-card-text>
	<v-data-table-server
		:headers="headers"
		:items="objects"
		@update:options="load"
		:items-length="parseInt(total)"
		:loading="loading"
		:show-select="showSelect"
		:single-select="singleSelect"
		v-model="selected"
		multi-sort
	>
		<template v-slot:top>
			<slot name=" batchActions" v-bind:selected="selected"></slot>
		</template>
			<template v-slot:item.status="{ item }">
				<enum-chip kind="status" :value="item.status" />
			</template>
		<template v-slot:item.name="{ item }">{{ item.name }}</template>
		<template v-slot:item.bean_id="{ item }">{{ item.bean?.name }}({{ item.bean?.id }})</template>
		<template v-slot:item.degree="{ item }">
			<roast-degree-badge v-if="item.degree" :degree="item.degree" />
			<span v-else class="text-medium-emphasis">—</span>
		</template>
		<template v-slot:item.beanqty="{ item }">{{ item.beanqty }}g</template>
		<template v-slot:item.linked_count="{ item }">
			<v-btn
				v-if="item.linked_count > 0"
				variant="text"
				size="small"
				color="primary"
				@click="$emit('view-roasts', item)"
			>
				{{ item.linked_count }}
			</v-btn>
			<span v-else class="text-medium-emphasis">0</span>
		</template>
		<template v-slot:item.action="{ item }">
			<slot name="actions" v-bind:item="item"></slot>
		</template>

		<template v-slot:footer>
			<slot name="batchActions" v-bind:selected="selected"></slot>
		</template>
	</v-data-table-server>
</template>

<script>
import BeanSelector from '../bean/Selector.vue'
import { clone, valuesIn, toPairsIn, filter, intersection } from 'lodash-es'
import qs from 'qs'
import { roastDegreeLabel } from './roastLabels'
import RoastDegreeBadge from './DegreeBadge.vue'
import EnumChip from '../common/EnumChip.vue'

export default {
	components: {
		BeanSelector,
		RoastDegreeBadge,
		EnumChip
	},
	props: {
		initQuery: { type: Object, default: null },
		title: { type: String, default: null },
		showSelect: { type: Boolean, default: false },
		singleSelect: { type: Boolean, default: true },
		headers: {
			type: Array,
			default: () => [
				{ title: '#', value: 'id' },
				{ title: '名称', align: 'left', sortable: true, value: 'name' },
				{ title: '生豆', value: 'bean_id' },
				{ title: '烘焙度', value: 'degree' },
				{ title: '烘焙数', value: 'linked_count', sortable: false },
				{ title: '状态', value: 'status' },
				{ title: '', align: 'left', sortable: false, value: 'action' }
			]
		}
	},
	emits: ['view-roasts'],
	data() {
		return {
			query: {},
			bean: null,
			currentQuery: {}, //当前结果的查询条件
			objects: [],
			total: 0,
			loading: false,
			options: {},
			refs: {},
			selected: [],
			errors: {},
			loadSeq: 0
		}
	},
	created() {
		// 必须在 data-table 首次 @update:options → load 之前写入，否则会丢 ids 等 initQuery
		if (this.initQuery) {
			this.query = clone(this.initQuery)
		}
	},
	mounted() {
		if (this.query.bean_id) {
			this.$api.bean.getById(this.query.bean_id, (res) => {
				this.onBeanSelected([res.id], [res])
			})
		}
		this.$api.common.meta('roast', 'RoastRecipeStatus', (res) => {
			this.refs = res
		})
	},
	computed: {
		queryValues() {
			return filter(valuesIn(this.query), (e) => !!e)
		},
		currentQueryValues() {
			return filter(valuesIn(this.currentQuery), (e) => !!e)
		},
		queryPairs() {
			return filter(toPairsIn(this.query), (e) => !!e[1])
		}
	},
	watch: {
		initQuery: {
			handler(nv) {
				this.query = clone(nv)
				if (this.query.bean_id) {
					this.$api.bean.getById(this.query.bean_id, (res) => {
						this.onBeanSelected([res.id], [res])
					})
				}
				this.load()
			},
			deep: true
		}
	},
	methods: {
		degreeLabel: roastDegreeLabel,
		onBeanSelected(ids, beans) {
			this.bean = beans[0]
			this.query.bean_id = this.bean.id
		},
		search() {
			if (
				this.queryValues.length !== this.currentQueryValues.length ||
				intersection(this.queryValues, this.currentQueryValues).length !== this.queryValues.length
			) {
				//条件变化
				if (this.options.page === 1) {
					//当前如果是第一页，手动触发
					this.load()
				} else {
					this.options.page = 1 //当前不是第一页，修改后通过watcher触发
				}
			} else {
				//条件没变，忽略
			}
		},
		reset() {
			this.query = {}
			this.bean = null
		},
		load(options = null) {
			if (!this.options) this.options = {}
			if (options) {
				this.options = options
			}

			let { sortBy, page, itemsPerPage } = this.options
			this.loading = true
			const seq = ++this.loadSeq
			let qwp = clone(this.query)
			qwp.pageno = page
			qwp.pagesize = itemsPerPage
			qwp.sortBy = 'updated_at'
			qwp.sortDesc = 'desc'
			if (sortBy && sortBy.length > 0) {
				qwp.sort = sortBy.map((e) => `${e.key} ${e.order}`).join(',')
				qwp.sortBy = sortBy[0]['key']
				qwp.sortDesc = sortBy[0]['order']
			}
			this.currentQuery = clone(this.query)

			this.$api.roast.searchRecipe(
				qwp,
				(response) => {
					if (seq !== this.loadSeq) return
					this.objects = response.objects
					this.total = response.total
					this.loading = false
				},
				(error) => {
					if (seq !== this.loadSeq) return
					this.errors = error.message
					this.loading = false
				}
			)
		}
	}
}
</script>

<style scoped>
.silent td {
	color: rgba(3, 3, 3, 0.5) !important;
}
</style>
